// src/pages/CadastroRateio.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';
import Formulario from '../../components/ui/Formulario';

import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import styles from './CadastroRateio.module.css';

// Validações e formatações
import { formatarPercentual } from '../../util/validacoes';

function CadastroRateio() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { get, post, put } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const idEmpresa = userData.empresa?.id || 0;

    const [usinas, setUsinas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [unidades, setUnidades] = useState([]);

    // formData: mantém ids como string para <select>
    const [formData, setFormData] = useState({
        idUsina: '',
        idUsuario: '',
        idUnidadeConsumidora: '',
        unidadeNome: '',
    });

    // estados do percentual
    const [percentualBruto, setPercentualBruto] = useState('');
    const [percentualFormatado, setPercentualFormatado] = useState('');

    // ---------------- helpers ----------------
    const fetchUsinas = async () => {
        const filtrosUsinas = new URLSearchParams({ idEmpresa }).toString();
        const s = await get(`/hello-sol/usinas/buscar?${filtrosUsinas}`);
        setUsinas(Array.isArray(s) ? s : (s.content ?? []));
    };

    const fetchClientesDaUsina = async (idUsinaSel) => {
        const data = await get(
            `/hello-sol/usuarios/buscarUsuarioUsina?` +
            new URLSearchParams({ idUsina: idUsinaSel, idTipoUsuario: 3 }).toString()
        );
        setUsuarios(Array.isArray(data) ? data : (data.content ?? []));
    };

    const fetchUnidadesDoCliente = async (idClienteSel) => {
        if (!idClienteSel) {
            setUnidades([]);
            return [];
        }
        const qs = new URLSearchParams({ idCliente: idClienteSel }).toString();
        const r = await get(`/hello-sol/unidades-consumidoras/buscar?${qs}`);
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setUnidades(lista);
        return lista;
    };

    // --------------- carregamentos ----------------

    // carrega usinas ao abrir
    useEffect(() => {
        if (!idEmpresa) return;
        (async () => {
            try {
                await fetchUsinas();
            } catch {
                exibirErro('Erro ao buscar usinas.');
            }
        })();
    }, [idEmpresa]); // eslint-disable-line react-hooks/exhaustive-deps

    // se for edição, carrega o rateio e pré-preenche
    useEffect(() => {
        if (!id) return;

        (async () => {
            try {
                const data = await get(`/hello-sol/rateios/${id}`);

                const idUsina = String(data?.usina?.id ?? '');
                const idCliente = String(data?.cliente?.id ?? '');
                const idUC = String(data?.unidadeConsumidora?.id ?? '');
                const nomeUC = data?.unidadeConsumidora?.nomeUnidade ?? '';

                // garantir que combos exibam os itens mesmo se a lista não tiver carregado ainda
                if (idUsina) {
                    setUsinas((curr) =>
                        curr.some((u) => String(u.id) === idUsina)
                            ? curr
                            : [{ id: Number(idUsina), nome: data?.usina?.nome || 'Usina' }, ...curr]
                    );
                }

                await fetchClientesDaUsina(idUsina);
                setUsuarios((curr) =>
                    curr.some((u) => String(u.id) === idCliente)
                        ? curr
                        : [{ id: Number(idCliente), nome: data?.cliente?.nome || 'Cliente' }, ...curr]
                );

                await fetchUnidadesDoCliente(idCliente);
                setUnidades((curr) =>
                    curr.some((uc) => String(uc.id) === idUC)
                        ? curr
                        : [{ id: Number(idUC), nomeUnidade: nomeUC }, ...curr]
                );

                // percentual vindo do back
                const num = Number(data?.percentual ?? 0);
                const limitado = Math.min(Math.max(num, 0), 100);

                setFormData({
                    idUsina,
                    idUsuario: idCliente,
                    idUnidadeConsumidora: idUC,
                    unidadeNome: nomeUC,
                });
                setPercentualBruto(limitado.toFixed(2));
                setPercentualFormatado(formatarPercentual(limitado));
            } catch {
                exibirErro('Erro ao carregar rateio.');
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // quando mudar a usina, zera cliente/uc e carrega clientes dessa usina
    useEffect(() => {
        // EM EDIÇÃO: não reagir (mantém o que veio do back)
        if (id) return;

        if (!formData.idUsina) {
            setUsuarios([]);
            setUnidades([]);
            setFormData((f) => ({ ...f, idUsuario: '', idUnidadeConsumidora: '', unidadeNome: '' }));
            return;
        }

        (async () => {
            try {
                await fetchClientesDaUsina(formData.idUsina);
                // ao trocar a usina, limpar dependentes
                setUnidades([]);
                setFormData((f) => ({ ...f, idUsuario: '', idUnidadeConsumidora: '', unidadeNome: '' }));
            } catch {
                exibirErro('Erro ao buscar clientes da usina.');
            }
        })();
    }, [formData.idUsina, id]); // eslint-disable-line react-hooks/exhaustive-deps

    // quando mudar o cliente, carrega unidades dele
    useEffect(() => {
        // EM EDIÇÃO: não reagir (mantém o que veio do back)
        if (id) return;

        if (!formData.idUsuario) {
            setUnidades([]);
            setFormData((f) => ({ ...f, idUnidadeConsumidora: '', unidadeNome: '' }));
            return;
        }

        (async () => {
            try {
                const lista = await fetchUnidadesDoCliente(formData.idUsuario);
                // opcional: se só existir 1 unidade, auto-seleciona
                if (lista.length === 1) {
                    setFormData((f) => ({
                        ...f,
                        idUnidadeConsumidora: String(lista[0].id),
                        unidadeNome: lista[0].nomeUnidade || '',
                    }));
                }
            } catch {
                exibirErro('Erro ao buscar unidades do cliente.');
            }
        })();
    }, [formData.idUsuario, id]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------- handlers ----------------

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const novo = { ...prev, [name]: value };

            if (name === 'idUsina') {
                novo.idUsuario = '';
                novo.idUnidadeConsumidora = '';
                novo.unidadeNome = '';
            }

            if (name === 'idUsuario') {
                novo.idUnidadeConsumidora = '';
                novo.unidadeNome = '';
            }

            if (name === 'idUnidadeConsumidora') {
                const sel = unidades.find((uc) => String(uc.id) === value);
                novo.unidadeNome = sel?.nomeUnidade || '';
            }

            return novo;
        });
    };

    // ao digitar percentual (livre, até 100)
    const onChangePercentual = (e) => {
        const raw = e.target.value.replace(/[^\d,\.]/g, '').replace(',', '.');
        const num = parseFloat(raw);
        if (!isNaN(num)) {
            const limitado = Math.min(num, 100);
            setPercentualBruto(limitado.toFixed(2));
            setPercentualFormatado(e.target.value);
        } else {
            setPercentualBruto('');
            setPercentualFormatado('');
        }
    };

    // ao sair do campo, formatamos bonito
    const onBlurPercentual = () => {
        if (percentualBruto !== '') {
            setPercentualFormatado(formatarPercentual(parseFloat(percentualBruto)));
        }
    };

    const salvarRateio = async () => {
        try {
            // validações básicas
            if (!formData.idUsina) return exibirErro('Selecione a Usina.');
            if (!formData.idUsuario) return exibirErro('Selecione o Cliente.');
            if (!formData.idUnidadeConsumidora) return exibirErro('Selecione a Unidade.');
            if (percentualBruto === '' || isNaN(Number(percentualBruto)))
                return exibirErro('Informe o Percentual.');

            const payload = {
                idUsina: Number(formData.idUsina),
                idUnidadeConsumidora: Number(formData.idUnidadeConsumidora),
                percentual: Number(percentualBruto),
            };

            if (id) await put(`/hello-sol/rateios/${id}`, { id: Number(id), ...payload });
            else await post(`/hello-sol/rateios`, payload);

            navigate('/home/listar-rateio');
        } catch (err) {
            exibirErro(err.message || 'Erro ao salvar o rateio.');
        }
    };

    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarRateio);

    return (
        <div className={styles_global.container}>
            <h2>{id ? 'Alterar Rateio' : 'Cadastrar Rateio'}</h2>

            <Formulario onSubmit={safeSubmit}>
                {/* Usina / Cliente */}
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsina">Usina*</label>
                        <select
                            id="idUsina"
                            name="idUsina"
                            className={styles.campoUsina}
                            value={formData.idUsina}
                            onChange={handleChange}
                            required
                            disabled={!!id} // desabilita em edição
                        >
                            <option value="">Selecionar Usina</option>
                            {usinas.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="idUsuario">Cliente*</label>
                        <select
                            id="idUsuario"
                            name="idUsuario"
                            className={styles.campoCliente}
                            value={formData.idUsuario}
                            onChange={handleChange}
                            required
                            disabled={!!id} // desabilita em edição
                        >
                            <option value="">Selecionar Cliente</option>
                            {usuarios.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Unidade / Percentual */}
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUnidadeConsumidora">Unidade*</label>
                        <select
                            id="idUnidadeConsumidora"
                            name="idUnidadeConsumidora"
                            className={styles.campoUnidade}
                            value={formData.idUnidadeConsumidora}
                            onChange={handleChange}
                            required
                            disabled={!!id} // desabilita em edição
                        >
                            <option value="">Selecionar Unidade</option>
                            {unidades.map((uc) => (
                                <option key={uc.id} value={String(uc.id)}>
                                    {uc.nomeUnidade}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="percentual">Percentual do Rateio*</label>
                        <input
                            id="percentual"
                            name="percentual"
                            value={percentualFormatado}
                            className={styles.campoPercentual}
                            onChange={onChangePercentual}
                            onBlur={onBlurPercentual}
                            placeholder="0,00 %"
                            maxLength={8}
                            required
                        />
                    </div>
                </div>

                {/* Botão */}
                <div className={styles_content.buttonGroup}>
                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </Formulario>
        </div>
    );
}

export default CadastroRateio;
